import {Checkbox, DatePicker, Select, Space, Button, Popover} from 'antd'
import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import {DeleteOutlined, FilterOutlined} from '@ant-design/icons'
import {PlantCollection} from '../../entities/plant'
import {useRouteLoaderData} from 'react-router-dom'
import {PlantedPlantsFilters} from '../../services/PlantsService'

dayjs.extend(weekday)

const dateFormat = 'YYYY/MM/DD'

// export type PlantsFilters = {
//   name?: string
//   startPlantingDate?: Date
//   endPlantingDate?: Date
//   projectId?: number
//   projects?: number[]
//   collections?: number[]
// }

export type PlantsFilterProps = {
  filters: PlantedPlantsFilters
  setFilters: (updater: (currentFilters: PlantedPlantsFilters) => PlantedPlantsFilters) => void
  userPlantCollections: PlantCollection[]
}

export function PlantsFilter(props: PlantsFilterProps) {
  const intl = useIntl()
  const {projectNames} = useRouteLoaderData('root') as {
    projectNames: {id: number; name: string}[]
  }
  const [open, setOpen] = useState(false)

  const hasDateFilter = !!(props.filters.startPlantingDate || props.filters.endPlantingDate)
  const hasProjectsFilter = !!(props.filters.projects && props.filters.projects.length > 0)
  const hasCollectionsFilter = !!(props.filters.collections && props.filters.collections.length > 0)
  const activeCount = [
    hasDateFilter,
    hasProjectsFilter,
    hasCollectionsFilter,
    !!props.filters.hideGardenPlanVariantPlants,
  ].filter(Boolean).length

  const content = (
    <div className="plants-filter__popover">
      <Space direction="vertical" style={{width: '100%'}} size="middle">
        <div>
          <div className="plants-filter__label">{intl.formatMessage({id: 'project'})}:</div>
          <Select
            mode="multiple"
            style={{width: '100%'}}
            allowClear
            value={props.filters.projects || []}
            onChange={(value) => props.setFilters((current) => ({...current, projects: value}))}
            options={projectNames.map((project) => ({label: project.name, value: project.id}))}
          />
        </div>
        <div>
          <div className="plants-filter__label">{intl.formatMessage({id: 'collections'})}:</div>
          <Select
            mode="multiple"
            style={{width: '100%'}}
            allowClear
            value={props.filters.collections || []}
            onChange={(value) => props.setFilters((current) => ({...current, collections: value}))}
            options={props.userPlantCollections.map((c) => ({label: c.name, value: c.id}))}
          />
        </div>
        <div>
          <div className="plants-filter__label">{intl.formatMessage({id: 'date'})}:</div>
          <DatePicker.RangePicker
            style={{width: '100%'}}
            allowEmpty={[true, true]}
            value={[
              props.filters.startPlantingDate ? dayjs(props.filters.startPlantingDate) : null,
              props.filters.endPlantingDate ? dayjs(props.filters.endPlantingDate) : null,
            ]}
            format={dateFormat}
            onChange={(values) =>
              props.setFilters((current) => ({
                ...current,
                startPlantingDate: values?.[0]?.toDate() || null,
                endPlantingDate: values?.[1]?.toDate() || null,
              }))
            }
            presets={[
              {
                label: intl.formatMessage({id: 'lastWeek'}),
                value: [dayjs().add(-7, 'day'), dayjs()],
              },
              {
                label: intl.formatMessage({id: 'lastMonth'}),
                value: [dayjs().add(-30, 'day'), dayjs()],
              },
              {
                label: intl.formatMessage({id: 'last90days'}),
                value: [dayjs().add(-90, 'day'), dayjs()],
              },
              {
                label: intl.formatMessage({id: 'lastYear'}),
                value: [dayjs().add(-365, 'day'), dayjs()],
              },
            ]}
          />
        </div>
        <Checkbox
          checked={!!props.filters.hideGardenPlanVariantPlants}
          onChange={(e) =>
            props.setFilters((current) => ({
              ...current,
              hideGardenPlanVariantPlants: e.target.checked,
            }))
          }
        >
          {intl.formatMessage({id: 'hideGardenPlanVariantPlants'})}
        </Checkbox>
      </Space>
    </div>
  )

  const title = (
    <div className="plants-filter__popover-title">
      <span>{intl.formatMessage({id: 'filtersUp'})}</span>
      {activeCount > 0 && (
        <a
          onClick={(e) => {
            e.stopPropagation()
            props.setFilters(() => ({
              name: '',
              projectId: undefined,
              startPlantingDate: undefined,
              endPlantingDate: undefined,
              collections: undefined,
              hideGardenPlanVariantPlants: undefined,
            }))
          }}
        >
          <DeleteOutlined /> {intl.formatMessage({id: 'clearFilters'})}
        </a>
      )}
    </div>
  )

  return (
    <Popover
      content={content}
      title={title}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <Button icon={<FilterOutlined />} type={activeCount > 0 ? 'primary' : 'default'}>
        {intl.formatMessage({id: 'filters'})}
        {activeCount > 0 && ` (${activeCount})`}
      </Button>
    </Popover>
  )
}
